/* eslint-disable @typescript-eslint/no-unused-vars */
import produce from 'immer';
import songsApi from 'services/api/songs/songsApi';
import { remixerApi } from 'services/api/remixer';
import userApi from 'services/api/user';
import distributionApi from 'services/api/distribution';
import aiMusicGeneratorApi from 'services/api/aiMusicGenerator/aiMusicGeneratorApi';
import { type DownloadLimit as ApiEntityDownloadLimit } from 'services/api/entities/user/privateUser';
import { isDownloadLimitError } from 'services/helpers/error';
import { Action, isActionOfType } from '../types';
import initialState from './initialState';
import { type UserStore, DownloadLimitStatus } from './types';
import {
  newSongsInLibraryFlagReset,
  setSongListPosition,
  decreaseAvailableAIGenerations,
  songsLibraryReset,
  songsLibrarySuccess,
  userReset,
  userSetSubscriptionActive,
  userSuccess,
} from './actions';
import type { UserActionTypes } from './actions';

const getDownloadLimit = (downloadLimit: ApiEntityDownloadLimit) => {
  if (!downloadLimit) return null;

  const { isWarning, remainingDownloads, daysToDownloadLimitReset } =
    downloadLimit;
  if (isWarning) {
    return {
      status:
        remainingDownloads > 0
          ? DownloadLimitStatus.Warning
          : DownloadLimitStatus.Disabled,
      remaining: remainingDownloads,
      daysToDownloadLimitReset,
    };
  }

  return {
    status: DownloadLimitStatus.Enabled,
    remaining: remainingDownloads,
    daysToDownloadLimitReset,
  };
};

const reducer = (
  state: UserStore = initialState,
  action: Action<UserActionTypes>
): UserStore => {
  if (isActionOfType(action, userSuccess)) {
    let subscription = null;
    let subscriptionType = null;
    if (state.subscription) {
      subscription = JSON.parse(JSON.stringify(state.subscription));
    }
    if (action.payload.subscription) {
      subscription = { ...action.payload.subscription };
    }
    if (state.subscriptionType) {
      subscriptionType = JSON.parse(JSON.stringify(state.subscriptionType));
    }
    if (action.payload.subscriptionType) {
      subscriptionType = action.payload.subscriptionType;
    }

    const {
      songDownloadLimit,
      stemsDownloadLimit,
      samplePackDownloadLimit,
      aiGenerationLimit,
      uploadAudioLimit,
      hasDistribution,
      artists,
      submitReleaseLimit,
      studioExportLimit,
    } = action.payload;

    return {
      ...state,
      ...action.payload,
      songDownloadLimit: songDownloadLimit
        ? getDownloadLimit(songDownloadLimit)
        : null,
      stemsDownloadLimit: stemsDownloadLimit
        ? getDownloadLimit(stemsDownloadLimit)
        : null,
      samplePackDownloadLimit: samplePackDownloadLimit
        ? getDownloadLimit(samplePackDownloadLimit)
        : null,
      aiGenerationLimit: aiGenerationLimit
        ? getDownloadLimit(aiGenerationLimit)
        : null,
      uploadAudioLimit: uploadAudioLimit
        ? getDownloadLimit(uploadAudioLimit)
        : null,
      subscription,
      subscriptionType,
      hasDistribution,
      artists,
      submitReleaseLimit: submitReleaseLimit
        ? getDownloadLimit(submitReleaseLimit)
        : null,
      studioExportLimit: studioExportLimit
        ? getDownloadLimit(studioExportLimit)
        : null,
    };
  }

  if (isActionOfType(action, userReset)) {
    return initialState;
  }

  if (isActionOfType(action, userSetSubscriptionActive)) {
    // Safe clone of subscription avoiding flow errors
    let subscription = null;
    if (state.subscription) {
      subscription = JSON.parse(JSON.stringify(state.subscription));
    }

    return {
      ...state,
      subscription: {
        ...subscription,
        status: 'active',
      },
    };
  }

  if (isActionOfType(action, songsLibrarySuccess)) {
    const { songs, paginationData } = action.payload;
    if (Object.keys(songs).length === 0) return state;

    return {
      ...state,
      songsLibrary: {
        ...state.songsLibrary,
        page: paginationData.currentPage,
        songs: {
          ...state.songsLibrary.songs,
          ...songs,
        },
        ids: [...state.songsLibrary.ids, ...Object.keys(songs)],
      },
    };
  }

  if (isActionOfType(action, songsLibraryReset)) {
    return {
      ...state,
      songsLibrary: {
        ...initialState.songsLibrary,
      },
    };
  }

  // reset flag not to refresh songs library list
  if (isActionOfType(action, newSongsInLibraryFlagReset)) {
    return {
      ...state,
      songsLibrary: {
        ...state.songsLibrary,
        newSongsInLibrary: false,
      },
    };
  }

  if (isActionOfType(action, setSongListPosition)) {
    const { position } = action.payload;
    return {
      ...state,
      songsLibrary: {
        ...state.songsLibrary,
        songListPosition: position,
      },
    };
  }

  if (songsApi.endpoints.addToLibrary.matchFulfilled(action)) {
    return produce(state, (draftState) => {
      draftState.songsLibrary.newSongsInLibrary = true;
    });
  }

  if (songsApi.endpoints.removeFromLibrary.matchFulfilled(action)) {
    const {
      meta: {
        arg: { originalArgs },
      },
    } = action;

    const { songId } = originalArgs;
    const { [songId]: removedSong, ...restOfSongs } = state.songsLibrary.songs;

    return {
      ...state,
      songsLibrary: {
        ...state.songsLibrary,
        songs: restOfSongs,
        ids: Object.keys(restOfSongs),
      },
    };
  }

  if (songsApi.endpoints.downloadSong.matchRejected(action)) {
    return produce(state, (draftState) => {
      const { payload } = action;
      const { songDownloadLimit } = draftState;
      if (songDownloadLimit && isDownloadLimitError(payload)) {
        songDownloadLimit.daysToDownloadLimitReset = payload.daysToReset;
      }
    });
  }

  if (songsApi.endpoints.downloadStems.matchRejected(action)) {
    return produce(state, (draftState) => {
      const { payload } = action;
      const { stemsDownloadLimit } = draftState;
      if (stemsDownloadLimit && isDownloadLimitError(payload)) {
        stemsDownloadLimit.daysToDownloadLimitReset = payload.daysToReset;
      }
    });
  }

  if (songsApi.endpoints.downloadSamplePack.matchRejected(action)) {
    return produce(state, (draftState) => {
      const { payload } = action;
      const { samplePackDownloadLimit } = draftState;
      if (samplePackDownloadLimit && isDownloadLimitError(payload)) {
        samplePackDownloadLimit.daysToDownloadLimitReset = payload.daysToReset;
      }
    });
  }

  if (aiMusicGeneratorApi.endpoints.getBundleById.matchFulfilled(action)) {
    return produce(state, (draftState) => {
      const {
        payload: { isFinished, songs },
      } = action;
      const { aiGenerationLimit } = draftState;
      if (aiGenerationLimit && isFinished) {
        aiGenerationLimit.remaining = Math.max(
          0,
          aiGenerationLimit.remaining - songs.length
        );
      }
    });
  }

  if (aiMusicGeneratorApi.endpoints.uploadUserLoop.matchFulfilled(action)) {
    return produce(state, (draftState) => {
      const { uploadAudioLimit } = draftState;
      if (uploadAudioLimit) {
        uploadAudioLimit.remaining -= 1;
      }
    });
  }

  if (isActionOfType(action, decreaseAvailableAIGenerations)) {
    const { aiGenerationLimit } = state;
    if (aiGenerationLimit) {
      return {
        ...state,
        aiGenerationLimit: {
          ...aiGenerationLimit,
          remaining: Math.max(0, aiGenerationLimit.remaining - 1),
        },
      };
    }
    return state;
  }

  if (userApi.endpoints.confirmEmail.matchFulfilled(action)) {
    return produce(state, (draftState) => {
      draftState.emailConfirmed = true;
    });
  }

  if (distributionApi.endpoints.setupArtistName.matchFulfilled(action)) {
    const {
      payload: artist,
      meta: {
        arg: { originalArgs },
      },
    } = action;

    const { save } = originalArgs;
    return produce(state, (draftState) => {
      if (save) {
        draftState.artists.push(artist);
      }
    });
  }

  if (remixerApi.endpoints.uploadSong.matchFulfilled(action)) {
    return produce(state, (draftState) => {
      if (!draftState.studioExportLimit) return;

      draftState.studioExportLimit.remaining -= 1;
    });
  }

  if (distributionApi.endpoints.submitRelease.matchFulfilled(action)) {
    return produce(state, (draftState) => {
      if (!draftState.submitReleaseLimit) return;

      draftState.submitReleaseLimit.remaining -= 1;
    });
  }

  return state;
};

export default reducer;
